/** "Work/Deep" → "Deep". A tag with no slashes is its own label. */
export function tagLabel(tag) {
    const parts = tag.split('/').filter(Boolean);
    return parts[parts.length - 1] ?? tag;
}
/**
 * Does `tag` match `filter`? True for an exact match and for any descendant,
 * so filtering by "Work" includes "Work/Deep" but not "Workshop".
 */
export function tagMatches(tag, filter) {
    if (tag === filter)
        return true;
    return tag.startsWith(`${filter}/`);
}
/** Does this task carry the tag (or any child of it)? */
export function taskHasTag(task, filter) {
    return (task.tags ?? []).some((tag) => tagMatches(tag, filter));
}
/**
 * Every tag in use, with how much is filed under it.
 *
 * Parent tags are synthesised: if anything is tagged `Work/Deep`, `Work`
 * appears too, because it's a real thing to filter by even if nothing carries
 * it literally. Sorted by open work first, then total, then name — the tag
 * with things left to do is the one you want at the front.
 */
export function collectTags(tasks, options = {}) {
    const { includeArchived = false, excludeHabits = false } = options;
    const counted = tasks
        .filter((t) => includeArchived || !t.is_archived)
        .filter((t) => !excludeHabits || !t.is_habit);
    const byTag = new Map();
    const bump = (tag, isOpen) => {
        const entry = byTag.get(tag) ?? { count: 0, open: 0 };
        entry.count += 1;
        if (isOpen)
            entry.open += 1;
        byTag.set(tag, entry);
    };
    for (const task of counted) {
        const isOpen = task.status !== 'completed';
        // A task tagged both Work and Work/Deep must only count once per level.
        const levels = new Set();
        for (const tag of task.tags ?? []) {
            if (!tag)
                continue;
            const parts = tag.split('/').filter(Boolean);
            for (let i = 1; i <= parts.length; i++) {
                levels.add(parts.slice(0, i).join('/'));
            }
        }
        for (const level of levels)
            bump(level, isOpen);
    }
    return [...byTag.entries()]
        .map(([tag, { count, open }]) => ({ tag, label: tagLabel(tag), count, open }))
        .sort((a, b) => {
        if (b.open !== a.open)
            return b.open - a.open;
        if (b.count !== a.count)
            return b.count - a.count;
        return a.tag.localeCompare(b.tag);
    });
}
/**
 * Tasks carrying every one of `filters` (AND, not OR).
 *
 * AND is the useful default for a small set of chips: adding a second tag
 * should narrow what you're looking at, which is what people expect from
 * clicking a second filter. An empty filter list means no filtering at all,
 * not "nothing matches".
 */
export function filterByTags(tasks, filters) {
    if (filters.length === 0)
        return [...tasks];
    return tasks.filter((task) => filters.every((filter) => taskHasTag(task, filter)));
}
//# sourceMappingURL=tags.js.map